import type {
    DayPart, CreateDayPart, SaveDayPart, DayPartOverrideInput,
} from '@/models/account-management/DayPart';
import { getJwt, HttpError } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

async function dayPartRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const hasBody = init?.body != null;
    const response = await fetch(`${BASE_URL}/${path}`, {
        ...init,
        headers: {
            'Authorization': `Bearer ${getJwt()}`,
            ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
            ...(init?.headers ?? {}),
        },
    });

    const contentType = response.headers.get('content-type') ?? '';
    const data = contentType.includes('json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) throw new HttpError(response.status, data);
    return data as T;
}

export const dayPartApi = {
    list: (brandId: string) =>
        dayPartRequest<DayPart[]>(`account-management/day-parts?brandId=${brandId}`),
    get: (id: string) =>
        dayPartRequest<DayPart>(`account-management/day-parts/${id}`),
    create: (item: CreateDayPart) =>
        dayPartRequest<{ id: string }>(`account-management/day-parts`, { method: 'POST', body: JSON.stringify(item) }),
    update: (id: string, item: SaveDayPart) =>
        dayPartRequest<void>(`account-management/day-parts/${id}`, { method: 'PUT', body: JSON.stringify(item) }),
    // Replaces the whole override set; an empty list clears every override.
    setOverrides: (id: string, overrides: DayPartOverrideInput[]) =>
        dayPartRequest<void>(`account-management/day-parts/${id}/overrides`, {
            method: 'PUT', body: JSON.stringify({ overrides }),
        }),
    remove: (id: string) =>
        dayPartRequest<void>(`account-management/day-parts/${id}`, { method: 'DELETE' }),
};

export const dayPartKeys = {
    /** dayPartApi.list(brandId) — invalidate after create / update / delete / override change.
     *
     *  ALSO invalidate `['promotion-schedules']` on any of those: a promotion schedule that references a day
     *  part renders its window FROM that day part, so moving one changes what the schedule list displays
     *  without the schedule itself changing at all. */
    list: (brandId?: string) => ['day-parts', brandId] as const,
};
